/**
 * src/lib/i18n/useContentLanguage.js — TICKET-959 (CEO grill 2026-09-27).
 *
 * The CONTENT language toggle for production screens: 'orig' (show what was written,
 * the default), 'en' or 'es'. Remembered per device across every screen. Separate from
 * the app's UI locale on purpose: the CEO chose a toggle on each screen, not the app
 * language. localStorage is best-effort (private mode / blocked storage -> 'orig').
 */
import { useState, useEffect, useCallback } from 'react';

export const CONTENT_LANG_KEY = 'acn.contentLang';
export const CONTENT_LANGS = Object.freeze(['orig', 'en', 'es']);
const EVENT = 'acn:content-lang';

export function readContentLang() {
  try {
    const v = globalThis.localStorage?.getItem(CONTENT_LANG_KEY);
    return CONTENT_LANGS.includes(v) ? v : 'orig';
  } catch {
    return 'orig'; // storage blocked: behave as a fresh device
  }
}

export function useContentLanguage() {
  const [lang, setLangState] = useState(readContentLang);

  // Every mounted toggle follows a change made on any screen of this device.
  useEffect(() => {
    const onChange = () => setLangState(readContentLang());
    globalThis.addEventListener?.(EVENT, onChange);
    globalThis.addEventListener?.('storage', onChange);
    return () => {
      globalThis.removeEventListener?.(EVENT, onChange);
      globalThis.removeEventListener?.('storage', onChange);
    };
  }, []);

  const setLang = useCallback((next) => {
    if (!CONTENT_LANGS.includes(next)) return;
    try { globalThis.localStorage?.setItem(CONTENT_LANG_KEY, next); } catch { /* storage blocked: in-memory only */ }
    setLangState(next);
    globalThis.dispatchEvent?.(new Event(EVENT));
  }, []);

  return { lang, setLang };
}
